import { useState, useEffect, useCallback } from "react"
import { useAuth } from "@/context/auth/auth-context"
import hive from "@/lib/api/hive"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Rss, Copy, Check, RefreshCw, ExternalLink } from "lucide-react"
import { Analytics } from "@/lib/analytics"

export function ICSSubscription() {
	const { token } = useAuth()
	const [icsUrl, setIcsUrl] = useState("")
	const [copied, setCopied] = useState(false)
	const [isLoading, setIsLoading] = useState(false)
	const [isRegenerating, setIsRegenerating] = useState(false)
	const [open, setOpen] = useState(false)

	const fetchSubscription = useCallback(async () => {
		if (!token) return
		setIsLoading(true)
		try {
			const data = await hive.getCalendarSubscription(token)
			setIcsUrl(data.url)
		} catch {
			// Silently fail — user can retry by reopening
		} finally {
			setIsLoading(false)
		}
	}, [token])

	useEffect(() => {
		if (open) fetchSubscription()
	}, [open, fetchSubscription])

	const handleCopy = async () => {
		await navigator.clipboard.writeText(icsUrl)
		setCopied(true)
		setTimeout(() => setCopied(false), 2000)
		Analytics.capture("ICS Feed Subscribed", { method: "copy" })
	}

	const handleRegenerate = async () => {
		if (!token) return
		setIsRegenerating(true)
		try {
			const data = await hive.regenerateCalendarSubscription(token)
			setIcsUrl(data.url)
		} catch {
			// Silently fail
		} finally {
			setIsRegenerating(false)
		}
	}

	const webcalUrl = icsUrl.replace(/^https?:\/\//, "webcal://")

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button variant="outline" size="sm" className="gap-2">
					<Rss className="h-4 w-4" />
					Subscribe
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-80" align="end">
				<div className="space-y-4">
					<div>
						<h4 className="font-semibold text-sm text-sky-950 dark:text-sky-50">Calendar Subscription</h4>
						<p className="text-xs text-muted-foreground mt-1">
							Subscribe to your dividend calendar in any calendar app.
						</p>
					</div>

					{isLoading ? (
						<div className="text-xs text-muted-foreground text-center py-2">Loading...</div>
					) : (
						<>
							<div className="flex gap-2">
								<Input
									readOnly
									value={icsUrl}
									className="text-xs h-8"
									onClick={(e) => (e.target as HTMLInputElement).select()}
								/>
								<Button variant="outline" size="sm" className="h-8 px-2 shrink-0" onClick={handleCopy}>
									{copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
								</Button>
							</div>

							<div className="space-y-2">
								<a
									href={webcalUrl}
									className="flex items-center gap-2 text-xs text-sky-700 dark:text-sky-300 hover:underline"
								>
									<ExternalLink className="h-3 w-3" />
									Apple Calendar / macOS
								</a>
								<a
									href={`https://calendar.google.com/calendar/r?cid=${encodeURIComponent(webcalUrl)}`}
									target="_blank"
									rel="noopener noreferrer"
									className="flex items-center gap-2 text-xs text-sky-700 dark:text-sky-300 hover:underline"
								>
									<ExternalLink className="h-3 w-3" />
									Google Calendar
								</a>
								<a
									href={webcalUrl}
									className="flex items-center gap-2 text-xs text-sky-700 dark:text-sky-300 hover:underline"
								>
									<ExternalLink className="h-3 w-3" />
									Outlook
								</a>
							</div>

							<div className="border-t border-sky-900/10 dark:border-sky-100/10 pt-3">
								<Button
									variant="ghost"
									size="sm"
									className="w-full text-xs h-7 text-muted-foreground hover:text-red-600"
									onClick={handleRegenerate}
									disabled={isRegenerating}
								>
									<RefreshCw className={`h-3 w-3 mr-1.5 ${isRegenerating ? "animate-spin" : ""}`} />
									Regenerate URL (invalidates old link)
								</Button>
							</div>
						</>
					)}
				</div>
			</PopoverContent>
		</Popover>
	)
}
